// client/state/HelpOverridesProvider.tsx
// Provider for runtime help text overrides

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { HelpHint } from "../ui/help/hints";

type Ctx = {
  resolve(helpId: string, fallback: string): string;
  version: string | null;
  refresh(): void;
};

const HelpCtx = createContext<Ctx>({
  resolve: (_id, fb) => fb,
  version: null,
  refresh: () => {}
});

export function HelpOverridesProvider({
  children,
  orgId,
  userId,
  locale = "en-US"
}: {
  children: React.ReactNode;
  orgId?: string;
  userId?: string;
  locale?: string;
}) {
  const [hints, setHints] = useState<Record<string, string>>({});
  const [version, setVersion] = useState<string | null>(null);

  const fetchHints = async () => {
    try {
      const url = `/api/v1/ui/help?locale=${encodeURIComponent(locale)}&orgId=${orgId || ""}&userId=${userId || ""}`;
      const res = await fetch(url);
      if (!res.ok) {
        // 404 is expected if help endpoint is not implemented - fail silently
        if (res.status !== 404) {
          console.warn(`Failed to fetch help hints: ${res.status}`);
        }
        return;
      }
      const json = await res.json();
      const map: Record<string, string> = {};
      for (const it of (json.items as HelpHint[])) {
        map[it.helpId] = it.text;
      }
      setHints(map);
      setVersion(json.version || null);
    } catch {
      // Network errors or other issues - fail silently for optional feature
    }
  };

  useEffect(() => {
    fetchHints();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchHints is stable, depends on orgId/userId/locale
  }, [orgId, userId, locale]);

  const ctx = useMemo<Ctx>(() => ({
    resolve: (helpId, fallback) => hints[helpId] ?? fallback,
    version,
    refresh: fetchHints
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchHints is stable
  }), [hints, version]);

  return <HelpCtx.Provider value={ctx}>{children}</HelpCtx.Provider>;
}

export function useHelpText() {
  return useContext(HelpCtx);
}
